import { EMPTY_COPY } from '../components/empty-copy'
import { EmptyState } from '../components/EmptyState'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Group,
  Menu,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertCircle, IconLayoutGrid, IconLock, IconPlus, IconRestore } from '@tabler/icons-react'
import { can } from '../api/session'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout'
import { fetchDashboard, saveDashboard } from '../api/dashboard'
import type { Dashboard, WidgetDefinition, WidgetPlacement } from '../api/types'
import { sessionQueryOptions } from '../session-store'
import { WidgetCard } from '../components/WidgetCard'
import { DashboardSkeleton } from '../components/Skeletons'

const ResponsiveGrid = WidthProvider(Responsive)

const GRID_COLUMNS = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }
const GRID_BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }
const ROW_HEIGHT = 84

/** How long to wait after the last drag before persisting the arrangement. */
const SAVE_DEBOUNCE_MS = 800

export function DashboardPage() {
  const queryClient = useQueryClient()
  const { data: user } = useQuery(sessionQueryOptions)

  const dashboard = useQuery({
    queryKey: ['dashboard'] as const,
    queryFn: ({ signal }) => fetchDashboard(signal),
  })

  // Held locally while the user drags, so the grid stays responsive and does
  // not wait on a round trip for every pixel of movement.
  const [placements, setPlacements] = useState<WidgetPlacement[] | null>(null)
  const saveTimer = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (dashboard.data) setPlacements(dashboard.data.layout)
  }, [dashboard.data])

  const save = useMutation({
    mutationFn: (layout: WidgetPlacement[]) => saveDashboard(layout),
    onSuccess: (updated) => {
      if (updated) queryClient.setQueryData<Dashboard>(['dashboard'], updated)
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Dashboard not saved',
        message: 'Your arrangement could not be saved. It will be restored on reload.',
      })
    },
  })

  const scheduleSave = useCallback(
    (next: WidgetPlacement[]) => {
      window.clearTimeout(saveTimer.current)
      saveTimer.current = window.setTimeout(() => save.mutate(next), SAVE_DEBOUNCE_MS)
    },
    [save],
  )

  // A pending save must not be lost because the user navigated away mid-debounce.
  useEffect(() => () => window.clearTimeout(saveTimer.current), [])

  const widgetsById = useMemo(() => {
    const map = new Map<string, WidgetDefinition>()
    for (const widget of dashboard.data?.widgets ?? []) map.set(widget.id, widget)
    return map
  }, [dashboard.data])

  const visible = useMemo(
    () => (placements ?? []).filter((p) => !p.hidden && widgetsById.has(p.widget_id)),
    [placements, widgetsById],
  )

  const hidden = useMemo(
    () => (placements ?? []).filter((p) => p.hidden && widgetsById.has(p.widget_id)),
    [placements, widgetsById],
  )

  const gridLayout: Layout[] = useMemo(
    () =>
      visible.map((p) => {
        const min = widgetsById.get(p.widget_id)?.minimum_size
        return {
          i: p.widget_id,
          x: p.column,
          y: p.row,
          w: p.columns,
          h: p.rows,
          minW: min?.columns ?? 2,
          minH: min?.rows ?? 2,
        }
      }),
    [visible, widgetsById],
  )

  const applyLayout = useCallback(
    (next: Layout[]) => {
      setPlacements((current) => {
        if (!current) return current

        const byId = new Map(next.map((item) => [item.i, item]))
        const updated = current.map((placement) => {
          const item = byId.get(placement.widget_id)
          if (!item) return placement
          return {
            ...placement,
            column: item.x,
            row: item.y,
            columns: item.w,
            rows: item.h,
          }
        })

        scheduleSave(updated)
        return updated
      })
    },
    [scheduleSave],
  )

  const setHidden = useCallback(
    (widgetId: string, isHidden: boolean) => {
      setPlacements((current) => {
        if (!current) return current
        const updated = current.map((p) => (p.widget_id === widgetId ? { ...p, hidden: isHidden } : p))
        scheduleSave(updated)
        return updated
      })
    },
    [scheduleSave],
  )

  const resetLayout = useCallback(() => {
    window.clearTimeout(saveTimer.current)
    // Saving an empty arrangement makes the server fall back to its generated
    // default, so "reset" needs no separate endpoint.
    save.mutate([], {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      },
    })
  }, [queryClient, save])

  /*
   * Said here rather than by redirecting.
   *
   * Everywhere else, a screen somebody may not use sends them to the
   * dashboard - so the dashboard cannot do the same thing without looping on
   * exactly the person who holds nothing. An explanation on a page they can
   * reach is the better answer anyway.
   */
  if (!can(user, 'dashboard.read')) {
    return (
      <Alert icon={<IconLock size={16} />} color="gray" variant="light">
        You do not have access to the dashboard. Ask an administrator if you need it.
      </Alert>
    )
  }

  if (dashboard.isPending) {
    return <DashboardSkeleton />
  }

  if (dashboard.isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Could not load your dashboard. Reload the page to try again.
      </Alert>
    )
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
        <Stack gap={2}>
          <Title order={2}>
            {greeting()}
            {user ? `, ${user.first_name}` : ''}
          </Title>
          <Text size="sm" c="dimmed">
            {visible.length === 0
              ? 'No widgets on your dashboard yet.'
              : `${visible.length} widget${visible.length === 1 ? '' : 's'} · drag a header to rearrange`}
          </Text>
        </Stack>

        <Group gap="xs">
          <Menu position="bottom-end" width={280} withArrow>
            <Menu.Target>
              <Button
                variant="default"
                leftSection={<IconPlus size={16} />}
                disabled={hidden.length === 0}
              >
                Add widget
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Hidden widgets</Menu.Label>
              {hidden.map((placement) => {
                const widget = widgetsById.get(placement.widget_id)
                return (
                  <Menu.Item
                    key={placement.widget_id}
                    leftSection={<IconLayoutGrid size={14} />}
                    onClick={() => setHidden(placement.widget_id, false)}
                  >
                    <Stack gap={0}>
                      <Text size="sm">{widget?.title}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {widget?.description}
                      </Text>
                    </Stack>
                  </Menu.Item>
                )
              })}
            </Menu.Dropdown>
          </Menu>

          <Button variant="subtle" color="gray" leftSection={<IconRestore size={16} />} onClick={resetLayout}>
            Reset
          </Button>
        </Group>
      </Group>

      {visible.length === 0 ? (
        <EmptyState
          topic="tasks"
          icon={<IconLayoutGrid size={26} />}
          title={EMPTY_COPY.dashboard.title}
          hint={EMPTY_COPY.dashboard.hint}
        />
      ) : (
        <Box mx={-8}>
          <ResponsiveGrid
            className="layout"
            layouts={{ lg: gridLayout, md: gridLayout, sm: gridLayout, xs: gridLayout, xxs: gridLayout }}
            breakpoints={GRID_BREAKPOINTS}
            cols={GRID_COLUMNS}
            rowHeight={ROW_HEIGHT}
            margin={[16, 16]}
            containerPadding={[8, 0]}
            draggableHandle=".medulla-widget-handle"
            onDragStop={applyLayout}
            onResizeStop={applyLayout}
            // Compacting vertically keeps the grid tidy without moving things
            // horizontally out from under the user.
            compactType="vertical"
            preventCollision={false}
          >
            {visible.map((placement) => {
              const widget = widgetsById.get(placement.widget_id)
              if (!widget) return null

              return (
                <div key={placement.widget_id}>
                  <WidgetCard definition={widget} onHide={() => setHidden(widget.id, true)} />
                </div>
              )
            })}
          </ResponsiveGrid>
        </Box>
      )}
    </Stack>
  )
}

function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}
